import { Record, type RecordOf } from 'immutable';
import { Interval } from 'luxon';

import type { GeneratedReportRanges, Timestep } from '../../utils/date';
import type { QcsSyncResourceStore } from '../core/resourceStoreTypes';
import type { Filter } from '../filters/filtersTypes';

export const REDUCER_KEY = 'reportConfigurationsStore';
export type REDUCER_KEY = typeof REDUCER_KEY;

export type AccountingType = 'ACCRUAL' | 'CASH';
export type ReportType = 'COMPARISON' | 'SUMMARY' | 'TRANSACTION';
export type ReportSubType =
  | 'BALANCE_SHEET'
  | 'BALANCES_OVER_TIME'
  | 'CUSTOM'
  | 'INCOME'
  | 'NET_INCOME'
  | 'PERFORMANCE_OVER_TIME'
  | 'SPENDING'
  | 'NET_WORTH'
  | 'TAX_SCHEDULE'
  | 'TAX_SUMMARY'
  | 'SAVINGS'
  | 'NONE';

// export type DateRange =
//     | 'ALL_DATES'
//     | 'THIS_MONTH'
//     | 'THIS_QUARTER'
//     | 'THIS_YEAR'
//     | 'LAST_MONTH'
//     | 'LAST_QUARTER'
//     | 'LAST_YEAR'
//     | 'MONTH_TO_DATE'
//     | 'QUARTER_TO_DATE'
//     | 'YEAR_TO_DATE'
//     | 'LAST_7_DAYS'
//     | 'LAST_14_DAYS'
//     | 'LAST_2_MONTHS'
//     | 'LAST_3_MONTHS'
//     | 'LAST_6_MONTHS'
//     | 'LAST_12_MONTHS'
//     | 'CUSTOM';

export type CompareDateRange = (GeneratedReportRanges & 'PRIOR_PERIOD') | 'PRIOR_YEAR';

export type RowType =
  | 'ACCOUNT'
  | 'BUSINESS'
  | 'CATEGORY'
  | 'CLIENT'
  | 'PROJECT'
  | 'PAYEE'
  | 'TAG'
  | 'TAX_LINE_ITEM'
  | 'TAX_SCHEDULE'
  | 'TIME'
  | 'USAGE'
  | 'NONE';
export type ColumnType =
  | 'ACCOUNT'
  | 'BUSINESS'
  | 'CLIENT'
  | 'PAYEE'
  | 'PROJECT'
  | 'TAG'
  | 'TIME'
  | 'TOTAL'
  | 'USAGE'
  | 'NONE';

export type ChartType =
  | 'BAR'
  | 'BAR_GROUPED'
  | 'BAR_GROUPED_HORIZONTAL'
  | 'BAR_SIDEWAYS'
  | 'BAR_STACKED'
  | 'MULTILINE'
  | 'NET_INCOME'
  | 'PIE'
  | 'NONE';

export type PersistentType = 'PREDEFINED' | 'CUSTOM' | 'TEMPORARY';
export type DensityType = 'COMPACT' | 'STANDARD' | 'COMFORTABLE';
export type GraphType = 'GRAPH_AND_GRID' | 'GRID' | 'GRAPH';

export type DisplayOptionsType = {
  hideCents: boolean;
  showExpensesAsNegative: boolean;
  alternateRows?: boolean;
  density?: DensityType;
  showOtherOnTop?: boolean;
  showSubTotalsOnTop?: boolean;
  showPLGraph?: boolean; // from pref
  showRowNumbers?: boolean;
};

export type OptionsType = {
  viewToShow?: GraphType;
  showInterval?: boolean;
  hideReportHeader?: boolean;
  showViewToShow?: boolean;
  showBreakdownBy?: boolean;
  showViewBy?: boolean;
};

export type DateRangeType = {
  id: GeneratedReportRanges;
  interval: Interval;
};

export type ReportNameType =
  | 'total-spending'
  | 'monthly-spending'
  | 'income'
  | 'net-income'
  | 'savings'
  | 'net-worth'
  | 'monthly-summary'
  | 'refund-tracker'
  | 'credit-score'
  | 'taxes'
  | `taxes-${string}`
  | 'personal-taxes'
  | 'custom'
  | 'profit-loss'
  | 'balance-sheet'
  | `balance-sheet-${string}`;

export type ReportConfigurationProps = {
  id?: string;
  clientId?: string;

  name: ReportNameType;
  usage?: string[];
  description?: string;

  accountingType: AccountingType;
  reportType: ReportType;
  reportSubType: ReportSubType;
  persistentType: PersistentType;
  options?: OptionsType;
  displayOptions?: DisplayOptionsType;

  dateRange: GeneratedReportRanges;
  startOn?: string;
  endOn?: string;

  rowType: RowType;
  columnType: ColumnType;
  timestep?: Timestep;

  // this is persisted in QCS
  filterId?: string;

  // this is only used as transient data in QCS by the generated-report APIs
  filter?: Filter | null;

  compareDateRange?: CompareDateRange;
  compareStartOn?: string;
  compareEndOn?: string;
  showSubTotalLabel?: boolean;
  showSubTotalsOnBottom?: boolean;
  showTotalRows?: boolean;
  isDeleted?: boolean;
  modifiedAt?: string;
  chartType?: ChartType;

  // this is only used by client
  location?: string;
};

export type ReportConfiguration = RecordOf<ReportConfigurationProps>;
export const ReportConfigurationRecord = Record<ReportConfigurationProps>({
  id: undefined,
  clientId: undefined,

  name: undefined!,
  description: undefined,
  usage: undefined,

  accountingType: 'CASH',
  reportType: undefined!,
  reportSubType: undefined!,
  persistentType: 'TEMPORARY',
  options: undefined,
  displayOptions: undefined,
  chartType: undefined,

  dateRange: undefined!,
  startOn: undefined,
  endOn: undefined,

  columnType: undefined!,
  rowType: undefined!,
  timestep: undefined,

  filterId: undefined,
  filter: undefined,

  isDeleted: undefined,
  modifiedAt: undefined,

  showSubTotalsOnBottom: undefined,
  showSubTotalLabel: undefined,
  showTotalRows: undefined,

  location: undefined,
});
export const mkReportConfiguration = (props: ReportConfigurationProps): ReportConfiguration =>
  ReportConfigurationRecord(props);

declare module 'app-components/utils/redux-store/rootState' {
  export default interface RootState {
    [REDUCER_KEY]: QcsSyncResourceStore<ReportConfigurationProps>;
  }
}
